import { api } from '@/shared/axios-client';

import { queryOptions, useQuery } from '@tanstack/react-query';

import type { ScmProviderId } from '@/types/scm-provider';

export interface Metrics {
  inventoryModeCoverage: number;
  linesOfCodeCoverage: number;
  lineCoverage: number;
  coveredLines: number;
  totalLines: number;
  testAutomationCount: number;
  manualTestCount: number;
}

export interface Inventory {
  files: number;
  classes: number;
  methods: number;
  functions: number;
  entities: number;
  executableLines: number;
  onboardedTestScriptCount?: number;
}

export interface OnboardedData {
  branchId: string;
  branchName: string;
  isDefault: boolean;
  orgId: string;
  orgName: string;
  repoId: string;
  repoName: string;
  repoStatus: string;
  pat: string;
  projectId: string;
  projectName: string;
  scanId: string;
  lastScanAt: string;
  repoUrl: string;
  pipelineId: string;
  prEmails: string[];
  metrics: Metrics;
  inventory: Inventory;
  dbRepoId: string;
  isView?: boolean;
  displayName: string;
  environment: string;
  frontendUrl: string;
  backendUrl: string;
  detectedTechnologies?: string[];
  isFrontend?: boolean;
  provider?: ScmProviderId;
  instrumentationPr?: {
    status?: string;
    prUrl?: string | null;
    prId?: number | null;
    branchName?: string | null;
    jobId?: string;
    mode?: string;
    targetBranch?: string | null;
    raisedAt?: string | null;
    failedAt?: string | null;
    error?: string | null;
    prMerged?: boolean | null;
  } | null;
  testInstrumentationPr?: OnboardedData['instrumentationPr'];
  captureEnabled?: boolean;
  isWebhookEnabled?: boolean;
  captureStatus?: string | null;
  comparisonStatus?: string | null;
  comparisonResult?: string | null;
  prStatus?: string | null;
  captureId?: string | null;
  captureOptedInBy?: string | null;
  captureUiState?: string | null;
  pendingDeletion?: {
    branchId?: string;
    status?: string;
    targets?: string[];
    mergedTargets?: string[];
    deinstrumentJobIds?: string[];
    requestedAt?: string;
    error?: string;
  } | null;
  onboardedAt?: string | null;
  isDeleted?: boolean;
  deletedAt?: string | null;
}

export interface apiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: [OnboardedData];
}

export const getComponentList = (): Promise<apiResponse> => {
  return api.get('/api/onboarded');
};

export const getComponentQueryOptions = () => {
  return queryOptions({
    queryKey: ['component-list'],
    queryFn: () => getComponentList(),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 5 * 60 * 100,
  });
};

export const useComponentList = () => {
  return useQuery({
    ...getComponentQueryOptions(),
  });
};
